import { getQueryParamValue } from '@@/api/mock-server/utils.js'
import type { MockServer } from '@@/api/mock-server/server.js'
import type { ConsultationReportItemModelBE } from '@/composables/api/ConsultationReport.js'
import { safeMathRandom } from '@/utils/number.js'

const CONSULTATION_STATUSES = [
  'CHECKED IN',
  'IN PROGRESS',
  'READY FOR DISCHARGE',
  'INVOICING',
  'DISCHARGED',
  'INVOICE PAID',
  'FINALIZED',
  'DRAFT',
] as const

const PATIENT_TYPES = ['First opinion', 'Referred patient'] as const
const VET_NAMES = [
  'Dr. Smith',
  'Dr. Johnson',
  'Dr. Williams',
  'Dr. Brown',
  'Dr. Jones',
]
const DEPARTMENTS = ['Main Clinic', 'Emergency', 'Surgery', 'Outpatient']
const COMPLAINTS = [
  'Lameness',
  'Vomiting',
  'Skin irritation',
  'Annual checkup',
  'Dental cleaning',
  'Vaccination',
  'Post-op follow-up',
  'Weight loss',
  'Coughing',
  null,
]

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(safeMathRandom() * arr.length)]
}

function generateMockData(count: number): Array<{
  id: string
  type: 'ConsultationMetrics'
  attributes: ConsultationReportItemModelBE
}> {
  return Array.from({ length: count }, (_, i) => {
    const day = Math.floor(safeMathRandom() * 28) + 1
    const month = Math.floor(safeMathRandom() * 3) + 1
    const dateStr = `2026-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const duration = Math.floor(safeMathRandom() * 55) + 5
    const waitTime = Math.floor(safeMathRandom() * 30)
    const totalInclVat = Math.round((safeMathRandom() * 500 + 50) * 100) / 100
    const amountPaid = Math.round(safeMathRandom() * totalInclVat * 100) / 100

    return {
      id: String(i + 1),
      type: 'ConsultationMetrics' as const,
      attributes: {
        consultation_id: 1000 + i,
        client_id: Math.floor(safeMathRandom() * 200) + 1,
        department_id: Math.floor(safeMathRandom() * 4) + 1,
        supervising_veterinarian_id: Math.floor(safeMathRandom() * 5) + 1,
        referring_vet_id: null,
        referring_organization_id: null,
        created_user_id: 1,
        modified_user_id: 1,
        department_name: pick(DEPARTMENTS),
        supervising_vet_name: pick(VET_NAMES),
        referring_vet_name: null,
        referring_org_name: null,
        consultation_created_date: dateStr,
        consultation_modified_date: dateStr,
        consultation_admitted_time_ts_utc: null,
        consultation_started_ts_utc: `${dateStr}T09:00:00Z`,
        consultation_finished_ts_utc: `${dateStr}T09:${String(duration).padStart(2, '0')}:00Z`,
        consultation_ended_ts_utc: `${dateStr}T10:00:00Z`,
        wait_time_minutes: waitTime,
        consultation_duration_minutes: duration,
        total_time_minutes: waitTime + duration,
        patient_type: pick(['FIRST_OPINION_CASE', 'REFERRED_PATIENT'] as const),
        patient_type_str: pick(PATIENT_TYPES),
        complaint: pick(COMPLAINTS),
        consultation_status_id: Math.floor(safeMathRandom() * 8) + 1,
        consultation_status: pick(CONSULTATION_STATUSES),
        client_type: pick(['FIRST TIME', 'REPEAT'] as const),
        is_first_time_client: safeMathRandom() > 0.5,
        total_incl_vat: totalInclVat,
        amount_paid: amountPaid,
        amount_due: Math.round((totalInclVat - amountPaid) * 100) / 100,
        invoice_count: Math.floor(safeMathRandom() * 3) + 1,
        has_invoice: true,
        payment_status: pick(['PAID', 'UNPAID', 'PART_PAID'] as const),
        report_to_authorities: null,
      },
    }
  })
}

const MOCK_DATA = generateMockData(85)

export function routes(server: MockServer) {
  server.get(
    '/reports_v2/consultation_metrics',
    function (_schema, request) {
      const pageNumber = parseInt(
        getQueryParamValue(request.queryParams['page[number]']) || '1',
      )
      const pageSize = parseInt(
        getQueryParamValue(request.queryParams['page[size]']) || '30',
      )

      let filtered = [...MOCK_DATA]

      const search = getQueryParamValue(request.queryParams['filter[search]'])
      if (search) {
        const term = search.toLowerCase()
        filtered = filtered.filter(
          (item) =>
            item.attributes.supervising_vet_name
              ?.toLowerCase()
              .includes(term) ||
            item.attributes.complaint?.toLowerCase().includes(term) ||
            String(item.attributes.consultation_id).includes(term),
        )
      }

      const totalCount = filtered.length
      const totalPages = Math.ceil(totalCount / pageSize)
      const start = (pageNumber - 1) * pageSize
      const paged = filtered.slice(start, start + pageSize)

      return {
        data: paged,
        meta: {
          pagination: {
            count: totalCount,
            pages: totalPages,
            page: pageNumber,
          },
        },
      }
    },
    { timing: 600 },
  )
}

export function seeds(_server: MockServer) {
  // No seeds needed — data is generated inline
}
